import { Link } from "react-router-dom";
import { useState } from "react";
import { ShieldCheck, ArrowUpRight, Mail, Phone, MapPin, Loader2, Check } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FinalCTA = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", property_type: "Luxury Homeowner", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError("Please complete the required fields.");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/consultations`, form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", property_type: "Luxury Homeowner", message: "" });
    } catch (err) {
      setStatus("idle");
      setError("Something went wrong. Please try again or email us directly.");
    }
  };

  return (
    <section id="contact" className="relative py-20 sm:py-24 md:py-40 overflow-hidden btr-bg-glow" data-testid="final-cta">
      <div className="btr-beam btr-pulse" style={{ width: 700, height: 700, top: "10%", left: "50%", transform: "translateX(-50%)", background: "#a2020b", opacity: 0.28 }} />
      <div className="absolute inset-0 btr-grid-animated opacity-30 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6">
        <div className="btr-reveal btr-glass rounded-2xl p-6 sm:p-8 md:p-14 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(162,2,11,0.15),transparent_60%)]" />
          <div className="absolute top-0 left-8 right-8 h-[1px] bg-gradient-to-r from-transparent via-[#a2020b]/60 to-transparent" />

          <div className="relative grid lg:grid-cols-[1.2fr_1fr] gap-12 items-start">
            {/* Left: copy */}
            <div>
              <span className="btr-badge">
                <span className="dot" />
                Schedule a Consultation
              </span>
              <h2 className="font-display font-extrabold text-white text-3xl sm:text-5xl lg:text-[3.4rem] mt-6 leading-[1.05] sm:leading-[1.02] tracking-tight">
                Do not wait for the loss to discover <span className="text-[#a2020b]">what was missing</span>.
              </h2>
              <p className="font-body text-zinc-400 text-base sm:text-lg mt-6 leading-relaxed">
                Before the loss. Before the litigation. Before the risk. Protect the asset, document the risk, and prepare before exposure becomes liability.
              </p>

              <div className="mt-9 space-y-4">
                <div className="flex items-center gap-3 text-zinc-300">
                  <div className="w-9 h-9 rounded-md bg-[#a2020b]/10 border border-[#a2020b]/30 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-[#a2020b]" />
                  </div>
                  <span className="font-body text-sm">Serving Florida &amp; Georgia</span>
                </div>
                <div className="flex items-center gap-3 text-zinc-300">
                  <div className="w-9 h-9 rounded-md bg-[#a2020b]/10 border border-[#a2020b]/30 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-[#a2020b]" />
                  </div>
                  <span className="font-body text-sm">contact@beforetherisk.com</span>
                </div>
                <div className="flex items-center gap-3 text-zinc-300">
                  <div className="w-9 h-9 rounded-md bg-[#a2020b]/10 border border-[#a2020b]/30 flex items-center justify-center">
                    <Phone className="w-4 h-4 text-[#a2020b]" />
                  </div>
                  <span className="font-body text-sm">By consultation — scheduled through form</span>
                </div>
              </div>
            </div>

            {/* Right: form */}
            <form onSubmit={submit} className="bg-[#050505]/60 border border-white/8 rounded-xl p-5 sm:p-6 space-y-4" data-testid="consultation-form">
              <div>
                <label className="font-display text-[11px] tracking-[0.2em] uppercase text-zinc-500">Full Name *</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="btr-input mt-1 w-full bg-[#0a0a0a] border border-white/8 focus:border-[#a2020b] focus:outline-none rounded-md px-4 py-3 text-white font-body text-sm transition-all"
                  placeholder="Jane Doe"
                  data-testid="form-name"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-display text-[11px] tracking-[0.2em] uppercase text-zinc-500">Email *</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="btr-input mt-1 w-full bg-[#0a0a0a] border border-white/8 focus:border-[#a2020b] focus:outline-none rounded-md px-4 py-3 text-white font-body text-sm transition-all"
                    placeholder="jane@firm.com"
                    data-testid="form-email"
                  />
                </div>
                <div>
                  <label className="font-display text-[11px] tracking-[0.2em] uppercase text-zinc-500">Phone</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="btr-input mt-1 w-full bg-[#0a0a0a] border border-white/8 focus:border-[#a2020b] focus:outline-none rounded-md px-4 py-3 text-white font-body text-sm transition-all"
                    placeholder="(555) 000-0000"
                    data-testid="form-phone"
                  />
                </div>
              </div>
              <div>
                <label className="font-display text-[11px] tracking-[0.2em] uppercase text-zinc-500">Property Type</label>
                <select
                  name="property_type"
                  value={form.property_type}
                  onChange={handleChange}
                  className="btr-input mt-1 w-full bg-[#0a0a0a] border border-white/8 focus:border-[#a2020b] focus:outline-none rounded-md px-4 py-3 text-white font-body text-sm transition-all"
                  data-testid="form-property-type"
                >
                  <option>Luxury Homeowner</option>
                  <option>HOA / Condominium</option>
                  <option>Commercial Property</option>
                  <option>Attorney / Legal Professional</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="font-display text-[11px] tracking-[0.2em] uppercase text-zinc-500">How can we help? *</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  required
                  className="btr-input mt-1 w-full bg-[#0a0a0a] border border-white/8 focus:border-[#a2020b] focus:outline-none rounded-md px-4 py-3 text-white font-body text-sm resize-none transition-all"
                  placeholder="Tell us briefly about the property, exposure, or objective."
                  data-testid="form-message"
                />
              </div>

              {error && (
                <div className="text-[#ff6b72] text-xs font-body" data-testid="form-error">
                  {error}
                </div>
              )}
              {status === "success" && (
                <div className="flex items-center gap-2 text-emerald-400 text-sm font-body" data-testid="form-success">
                  <Check className="w-4 h-4" />
                  Consultation request received. Our team will be in touch shortly.
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="submit"
                  className="btr-btn-primary btr-cta-breathe w-full sm:w-auto"
                  disabled={status === "loading"}
                  data-testid="form-submit"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4" /> Schedule Consultation
                    </>
                  )}
                </button>
                <Link to="/risk-score" className="btr-btn-ghost w-full sm:w-auto" data-testid="form-secondary-cta">
                  Take the Risk Score <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
